/** Orden de anidamiento del diseño §11. El editor es el más interno y gana siempre. */
export const SCOPES = ['list', 'panel', 'overlay', 'editor'] as const;
export type Scope = (typeof SCOPES)[number];

/**
 * Pila de scopes activos. Se apila al abrir el panel, un popover o el editor, y se
 * desapila al cerrarlos. `list` está siempre en el fondo porque la vista de fondo
 * nunca desaparece.
 */
export class ScopeStack {
  private entries: { id: number; scope: Scope }[] = [];
  private nextId = 1;
  private listeners = new Set<() => void>();
  private cachedChain: Scope[] = ['list'];

  push(scope: Scope): () => void {
    const id = this.nextId++;
    this.entries.push({ id, scope });
    this.emit();
    return () => {
      const before = this.entries.length;
      this.entries = this.entries.filter((entry) => entry.id !== id);
      if (this.entries.length !== before) this.emit();
    };
  }

  /** El scope que resuelve las teclas ahora mismo. */
  top(): Scope {
    return this.entries.at(-1)?.scope ?? 'list';
  }

  /**
   * De más interno a más externo: el orden en el que se busca un acorde.
   * La referencia solo cambia cuando cambia la pila, para poder pasarla a
   * `useSyncExternalStore` sin provocar un bucle de renders.
   */
  chain(): Scope[] {
    return this.cachedChain;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    this.cachedChain = this.computeChain();
    for (const listener of this.listeners) listener();
  }

  private computeChain(): Scope[] {
    const seen = new Set<Scope>();
    const chain: Scope[] = [];
    for (let i = this.entries.length - 1; i >= 0; i--) {
      const scope = this.entries[i]?.scope;
      if (scope !== undefined && !seen.has(scope)) {
        seen.add(scope);
        chain.push(scope);
      }
    }
    if (!seen.has('list')) chain.push('list');
    return chain;
  }
}
